import { baseApi } from '../api/baseApi'
import { PLATFORM_MAINTENANCE_URL, PLATFORM_MAINTENANCE_WORKPLACES_URL, platformMaintenanceWorkplaceUrl } from '../api/urls'
import type { MaintenanceDto } from './maintenanceSlice'

/** Тег кэша состояния режима обновления: событие живого канала и восстановление связи его перечитывают. */
export const MAINTENANCE_TAG = 'Maintenance'

/**
 * Тип общесистемного события живого канала «режим обновления изменился» (сервер — `LiveEventTypes.ServiceMaintenanceChanged`):
 * приходит без привязки к ресурсу и означает «перечитай состояние режима».
 */
export const SERVICE_MAINTENANCE_CHANGED = 'service.maintenance.changed'

/** «Список открытых РМК изменился» (сервер — `LiveEventTypes.ServiceMaintenanceWorkplacesChanged`): пульт перечитывает список. */
export const SERVICE_MAINTENANCE_WORKPLACES_CHANGED = 'service.maintenance.workplaces.changed'

/** Состояние рабочего места для пульта: корзина пуста, набирается корзина, идёт оплата/пробитие, заблокировано на время обновления. */
export type MaintenanceWorkplaceState = 'idle' | 'cart' | 'paying' | 'locked'

const WORKPLACE_STATES: readonly MaintenanceWorkplaceState[] = ['idle', 'cart', 'paying', 'locked']

/** Открытая РМК в списке пульта обновления. `silent` — давно не сообщала о себе («нет связи»). */
export interface MaintenanceWorkplace {
  workplaceId: string
  resourceId: string | null
  label: string
  person: string
  state: MaintenanceWorkplaceState
  stateSince: string
  lastSeenAt: string
  silent: boolean
}

/** Что открытая РМК сообщает о себе. */
export interface MaintenanceWorkplaceReport {
  workplaceId: string
  resourceId: string | null
  label: string
  state: MaintenanceWorkplaceState
}

/** Просьба включить или выключить режим; текст — что увидят кассиры (дословно), необязателен. */
export interface MaintenanceUpdate {
  active: boolean
  message?: string | null
}

/** Чужая форма ответа — выключенный режим: клиент не падает от неожиданного тела, а считает, что обновления нет. */
function decodeMaintenance(raw: unknown): MaintenanceDto {
  const value = typeof raw === 'object' && raw !== null ? (raw as Record<string, unknown>) : {}
  return {
    active: value.active === true,
    message: typeof value.message === 'string' && value.message.trim() !== '' ? value.message : null,
    since: typeof value.since === 'string' && value.since !== '' ? value.since : null,
  }
}

const text = (value: unknown): string => (typeof value === 'string' ? value : '')

/** Список открытых РМК; запись без рабочего места или с незнакомым состоянием пропускается, а не роняет пульт. */
export function decodeMaintenanceWorkplaces(raw: unknown): MaintenanceWorkplace[] {
  if (!Array.isArray(raw)) throw new Error('пульт обновления: ожидался список рабочих мест')
  return raw.flatMap((item): MaintenanceWorkplace[] => {
    if (typeof item !== 'object' || item === null) return []
    const value = item as Record<string, unknown>
    const state = WORKPLACE_STATES.find((known) => known === value.state)
    const workplaceId = text(value.workplaceId)
    if (state === undefined || workplaceId === '') return []
    return [
      {
        workplaceId,
        resourceId: typeof value.resourceId === 'string' && value.resourceId !== '' ? value.resourceId : null,
        label: text(value.label),
        person: text(value.person),
        state,
        stateSince: text(value.stateSince),
        lastSeenAt: text(value.lastSeenAt),
        silent: value.silent === true,
      },
    ]
  })
}

export const maintenanceApi = baseApi.enhanceEndpoints({ addTagTypes: [MAINTENANCE_TAG] }).injectEndpoints({
  endpoints: (build) => ({
    /**
     * Состояние режима обновления (RP-MNT). `readOnly` — GET можно безопасно повторить при сбое сети; `background` — опрос режима не
     * трогает окно связи (оно про действия человека, а не про фоновую проверку). Свежесть держат событие живого канала и восстановление
     * связи, перечитывая тег, а не таймер.
     */
    maintenanceState: build.query<MaintenanceDto, void>({
      query: () => ({ url: PLATFORM_MAINTENANCE_URL, decode: decodeMaintenance, readOnly: true, background: true }),
      providesTags: [{ type: MAINTENANCE_TAG, id: 'STATE' }],
      keepUnusedDataFor: 24 * 60 * 60,
    }),
    // Включить/выключить режим: право проверяет сервер (platform:maintenance:manage). Успех перечитывает состояние и шлёт событие всем.
    maintenanceSet: build.mutation<MaintenanceDto, MaintenanceUpdate>({
      query: (body) => ({ url: PLATFORM_MAINTENANCE_URL, method: 'PUT', body, decode: decodeMaintenance }),
      invalidatesTags: [{ type: MAINTENANCE_TAG, id: 'STATE' }],
    }),
    /** Пульт обновления: открытые РМК и их состояние (право управлять режимом). Свежесть — событие живого канала. */
    maintenanceWorkplaces: build.query<MaintenanceWorkplace[], void>({
      query: () => ({ url: PLATFORM_MAINTENANCE_WORKPLACES_URL, decode: decodeMaintenanceWorkplaces, readOnly: true }),
      providesTags: [{ type: MAINTENANCE_TAG, id: 'WORKPLACES' }],
    }),
    /** Рабочее место сообщает о себе: фоновый запрос — сбой не трогает окно связи и действия кассира, следующий пульс повторит. */
    maintenanceReport: build.mutation<null, MaintenanceWorkplaceReport>({
      query: (body) => ({ url: PLATFORM_MAINTENANCE_WORKPLACES_URL, method: 'POST', body, background: true, decode: () => null }),
    }),
    /** РМК ушла с экрана: пульт убирает её сразу, а не по молчанию. */
    maintenanceLeave: build.mutation<null, { workplaceId: string }>({
      query: ({ workplaceId }) => ({ url: platformMaintenanceWorkplaceUrl(workplaceId), method: 'DELETE', background: true, decode: () => null }),
    }),
  }),
})

/** Перечитать состояние режима: по событию живого канала и после восстановления связи. */
export const MAINTENANCE_INVALIDATION = [{ type: MAINTENANCE_TAG, id: 'STATE' }] as const

/** Перечитать список открытых РМК пульта: по событию живого канала и после восстановления связи. */
export const MAINTENANCE_WORKPLACES_INVALIDATION = [{ type: MAINTENANCE_TAG, id: 'WORKPLACES' }] as const

export const {
  useMaintenanceStateQuery,
  useMaintenanceSetMutation,
  useMaintenanceWorkplacesQuery,
  useMaintenanceReportMutation,
  useMaintenanceLeaveMutation,
} = maintenanceApi
